import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Card, Col, Container, Form, Modal, OverlayTrigger, Row, Spinner, Table, Tooltip } from 'react-bootstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator'
import ToolkitProvider from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min'
import * as XLSX from 'xlsx'
import { SiMicrosoftexcel } from 'react-icons/si'
import { FaSort, FaSortUp, FaSortDown, FaPencilAlt } from 'react-icons/fa'
import { Auth } from 'recupero-comunes'
import { ObtenerCompanias } from '../../../store/actions/encargado-cia-action'
import { buscarPagosAprobadosAction, obtenerEstadoEmisionAction, emitirPagosAction, obtenerOpcionesReversaAction, reversarRespuestaAction } from '../../../store/actions/emision-pagos-action'

const POLL_INTERVAL_MS = 5000
const POLL_MAX_INTENTOS = 12

const rowKey = (row) => `${row.idCaso}_${row.idImplicado}`

const EmisionPagosCias = () => {
    const dispatch = useDispatch()
    const { rutUsuario, rutOrganizacion } = Auth.currentUser()

    const dataCompanias = useSelector((state) => state.encargado_compania)
    const listaCompanias = dataCompanias?.companias?.companias || []

    const dataEmisionPagos = useSelector((state) => state.emisionPagosReducer)
    const { pagosAprobados } = dataEmisionPagos

    const [filtros, setFiltros] = useState({ fechaDesde: '', fechaHasta: '', companiaId: '' })
    const [errorFechas, setErrorFechas] = useState('')
    const [cargando, setCargando] = useState(false)
    const [seleccionados, setSeleccionados] = useState({})
    const [showConfirm, setShowConfirm] = useState(false)
    const [emitiendo, setEmitiendo] = useState(false)
    const [resultadoEmision, setResultadoEmision] = useState(null)
    const [resumenEmisionActiva, setResumenEmisionActiva] = useState([])
    const [urlExcel, setUrlExcel] = useState(null)
    const [refrescandoExcel, setRefrescandoExcel] = useState(false)

    const [showReversa, setShowReversa] = useState(false)
    const [filaReversa, setFilaReversa] = useState(null)
    const [opcionesReversa, setOpcionesReversa] = useState([])
    const [opcionRevSeleccionada, setOpcionRevSeleccionada] = useState(null)
    const [obsReversa, setObsReversa] = useState('')
    const [reversando, setReversando] = useState(false)

    const pollRef = useRef({ intentos: 0, timer: null })

    useEffect(() => {
        dispatch(ObtenerCompanias(Buffer.from(JSON.stringify({})).toString('base64')))
        buscar()
        return () => detenerPolling()
    }, [])

    const detenerPolling = () => {
        if (pollRef.current.timer) {
            clearInterval(pollRef.current.timer)
            pollRef.current.timer = null
        }
    }

    const buscar = () => {
        if (filtros.fechaDesde && filtros.fechaHasta && filtros.fechaDesde > filtros.fechaHasta) {
            setErrorFechas('"Aprobado Desde" no puede ser posterior a "Aprobado Hasta".')
            return
        }
        setErrorFechas('')
        setSeleccionados({})
        let parametros = {}
        if (filtros.fechaDesde) {
            parametros.fechaAprobDesde = filtros.fechaDesde
        }
        if (filtros.fechaHasta) {
            parametros.fechaAprobHasta = filtros.fechaHasta
        }
        if (filtros.companiaId) {
            parametros.compania = { id: filtros.companiaId }
        }
        let query = Buffer.from(JSON.stringify(parametros)).toString('base64')
        setCargando(true)
        dispatch(buscarPagosAprobadosAction(query))
            .then(() => setCargando(false))
            .catch(() => setCargando(false))
    }

    // Tras Emitir Pagos, la sección "Crear Emisión de Pagos" queda congelada (Id/Monto/Cantidad/resumen
    // de la emisión recién creada) y la grilla bloqueada para nueva selección hasta presionar Limpiar.
    const handleLimpiar = () => {
        setFiltros({ fechaDesde: '', fechaHasta: '', companiaId: '' })
        setErrorFechas('')
        detenerPolling()
        setResultadoEmision(null)
        setResumenEmisionActiva([])
        setUrlExcel(null)
        setRefrescandoExcel(false)
        setSeleccionados({})
    }

    const filas = pagosAprobados || []

    const filasSeleccionadas = useMemo(
        () => filas.filter((row) => seleccionados[rowKey(row)]),
        [filas, seleccionados]
    )

    const montoEmitir = filasSeleccionadas.reduce((total, row) => total + Number(row.montoPago || 0), 0)
    const cantidadPagos = filasSeleccionadas.length

    const resumenPorCompania = useMemo(() => {
        const agrupado = {}
        filasSeleccionadas.forEach((row) => {
            const key = row.companiaTercero || 'Sin Compañía'
            if (!agrupado[key]) agrupado[key] = { compania: key, monto: 0, cantidad: 0 }
            agrupado[key].monto += Number(row.montoPago || 0)
            agrupado[key].cantidad += 1
        })
        return Object.values(agrupado)
    }, [filasSeleccionadas])

    // Con una emisión activa (entre "Emitir Pagos" y "Limpiar"), se muestran los valores congelados
    // de esa emisión en vez de los recalculados en vivo desde la selección (que queda vacía).
    const montoMostrado = resultadoEmision ? resultadoEmision.montoEmitido : montoEmitir
    const cantidadMostrada = resultadoEmision ? resultadoEmision.cantidadPagos : cantidadPagos
    const resumenMostrado = resultadoEmision ? resumenEmisionActiva : resumenPorCompania

    const handleSelectRow = (row, isSelect) => {
        setSeleccionados((prev) => ({ ...prev, [rowKey(row)]: isSelect }))
    }
    const handleSelectAllRows = (isSelect) => {
        if (!isSelect) { setSeleccionados({}); return }
        const nuevo = {}
        filas.filter(r => r.indEmitepago).forEach((row) => { nuevo[rowKey(row)] = true })
        setSeleccionados(nuevo)
    }

    const handleDescargarExcelBusqueda = () => {
        const hoy = new Date()
        const aaaa = hoy.getFullYear()
        const mm = String(hoy.getMonth() + 1).padStart(2, '0')
        const dd = String(hoy.getDate()).padStart(2, '0')
        const nombreArchivo = `Cartas aprobadas sin emitir ${aaaa}${mm}${dd}`
        const datos = filas.map((row) => ({
            'Siniestro CIA': row.siniestroCIA,
            'Fec.Siniestro': formatearFecha(row.fecSiniestroCIA),
            'Ing.Carta': formatearFecha(row.fecIngCarta),
            'Compañía Tercero': row.companiaTercero,
            'Siniestro Tercero': row.siniestroTercero,
            'Fec.Aprobación': formatearFecha(row.fecAprobado),
            'Monto Pago': Number(row.montoPago || 0),
            'Motivo Aprobación': row.motivoAprobado
        }))
        const ws = XLSX.utils.json_to_sheet(datos)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Cartas aprobadas')
        XLSX.writeFile(wb, `${nombreArchivo}.xlsx`)
    }

    const headerStyleGrilla = { backgroundColor: '#728bb0', color: 'white' }

    const formatearFecha = (cell) => {
        if (!cell) return ''
        const fecha = new Date(cell)
        if (isNaN(fecha.getTime())) return cell
        const dia = String(fecha.getUTCDate()).padStart(2, '0')
        const mes = String(fecha.getUTCMonth() + 1).padStart(2, '0')
        const anio = fecha.getUTCFullYear()
        return `${dia}-${mes}-${anio}`
    }

    const sortHeaderFormatter = (column, colIndex, { sortElement }) => (
        <div>{column.text}{sortElement}</div>
    )

    const sortCaretGrilla = (order) => {
        if (!order) return <FaSort className='ms-1' style={{ color: 'white' }} />
        if (order === 'asc') return <FaSortUp className='ms-1' style={{ color: 'white' }} />
        return <FaSortDown className='ms-1' style={{ color: 'white' }} />
    }

    const sortNumerico = (a, b, order) => {
        const diff = Number(a || 0) - Number(b || 0)
        return order === 'asc' ? diff : -diff
    }

    const columns = [
        {
            dataField: 'siniestroCIA', text: 'Siniestro CIA', sort: true, sortCaret: sortCaretGrilla, sortFunc: sortNumerico, headerStyle: headerStyleGrilla, headerFormatter: sortHeaderFormatter,
            formatter: (cell, row) => (
                <OverlayTrigger overlay={<Tooltip>ID Caso: {row.idCaso}</Tooltip>}>
                    <span style={{ cursor: 'help' }}>{cell}</span>
                </OverlayTrigger>
            )
        },
        { dataField: 'fecSiniestroCIA', text: 'Fec.Siniestro', sort: true, sortCaret: sortCaretGrilla, headerStyle: headerStyleGrilla, headerFormatter: sortHeaderFormatter, formatter: formatearFecha },
        { dataField: 'fecIngCarta', text: 'Fec.Ing.Carta', sort: true, sortCaret: sortCaretGrilla, headerStyle: headerStyleGrilla, headerFormatter: sortHeaderFormatter, formatter: formatearFecha },
        { dataField: 'companiaTercero', text: 'Compañía Ter', sort: true, sortCaret: sortCaretGrilla, headerStyle: headerStyleGrilla, headerFormatter: sortHeaderFormatter },
        { dataField: 'siniestroTercero', text: 'Siniestro Ter', sort: true, sortCaret: sortCaretGrilla, sortFunc: sortNumerico, headerStyle: headerStyleGrilla, headerFormatter: sortHeaderFormatter },
        { dataField: 'fecAprobado', text: 'Fec.Aprob.', sort: true, sortCaret: sortCaretGrilla, headerStyle: headerStyleGrilla, headerFormatter: sortHeaderFormatter, formatter: formatearFecha },
        {
            dataField: 'montoPago', text: 'Monto Pago', sort: true, sortCaret: sortCaretGrilla, sortFunc: sortNumerico, align: 'right', headerAlign: 'right', headerStyle: headerStyleGrilla, headerFormatter: sortHeaderFormatter,
            formatter: (cell) => Number(cell || 0).toLocaleString('es-CL')
        },
        {
            dataField: 'motivoAprobado', text: 'Motivo Aprob.', sort: true, sortCaret: sortCaretGrilla, headerFormatter: sortHeaderFormatter,
            headerStyle: { ...headerStyleGrilla, width: '220px' },
            style: { whiteSpace: 'normal' }
        },
        {
            dataField: '__accion', isDummyField: true, text: '', headerStyle: headerStyleGrilla,
            style: { width: '38px', textAlign: 'center', padding: '4px' },
            formatter: (cell, row) => (
                <OverlayTrigger overlay={<Tooltip>Gestionar respuesta</Tooltip>}>
                    <Button variant='outline-secondary' size='sm' className='p-1'
                        onClick={() => handleAbrirReversa(row)}>
                        <FaPencilAlt size={11} />
                    </Button>
                </OverlayTrigger>
            )
        }
    ]

    const calcularDias = (fecAprobado) => {
        if (!fecAprobado) return ''
        const diff = Math.floor((Date.now() - new Date(fecAprobado).getTime()) / 86400000)
        return diff
    }

    const handleAbrirReversa = (row) => {
        setFilaReversa(row)
        setOpcionRevSeleccionada(null)
        setObsReversa('')
        setOpcionesReversa([])
        setShowReversa(true)

        dispatch(obtenerOpcionesReversaAction({
            accion: 'obtenerOpcionesReversa',
            clasRpta: row.clasRpta,
            motivoRpta: row.motivoRpta
        })).then(ra => setOpcionesReversa(ra?.payload || []))
    }

    const handleConfirmarReversa = () => {
        if (!opcionRevSeleccionada || reversando) return
        setReversando(true)
        dispatch(reversarRespuestaAction({
            accion: 'reversarRespuesta',
            idCaso: filaReversa.idCaso,
            idImplicado: filaReversa.idImplicado,
            motivoRptaOrigen: filaReversa.motivoRpta,
            clasRptaOrigen: filaReversa.clasRpta,
            motivoDestino: opcionRevSeleccionada.motivoDestino,
            clasDestino: opcionRevSeleccionada.clasDestino,
            tareaReversa: opcionRevSeleccionada.tareaReversa,
            flujoReversa: opcionRevSeleccionada.flujoReversa,
            procesoReversa: opcionRevSeleccionada.procesoReversa,
            observaciones: obsReversa,
            idOrganizacion: rutOrganizacion,
            idUsuario: rutUsuario
        }))
            .then(() => {
                setShowReversa(false)
                setReversando(false)
                buscar()
            })
            .catch(error => {
                setReversando(false)
                alert('Error al reversar: ' + error.message)
            })
    }

    const handleEmitirPagosClick = () => {
        if (!(montoEmitir > 0)) return
        setShowConfirm(true)
    }

    const handleConfirmarEmision = () => {
        setShowConfirm(false)
        setEmitiendo(true)
        const body = {
            idOrganizacion: rutOrganizacion,
            idUsuario: rutUsuario,
            pagos: filasSeleccionadas.map((row) => ({ idCaso: row.idCaso, idImplicado: row.idImplicado }))
        }
        dispatch(emitirPagosAction(body))
            .then(() => setEmitiendo(false))
            .catch((error) => {
                setEmitiendo(false)
                alert('Error al emitir pagos: ' + error.message)
            })
    }

    // El resultado real (resuelto por redux-promise) llega como payload de POST_EMITE_PAGOS al store;
    // se consume vía reducer/useSelector en vez de esperar el valor de retorno de dispatch().
    useEffect(() => {
        if (dataEmisionPagos.emisionRealizada) {
            setResultadoEmision(dataEmisionPagos.emisionRealizada)
            setResumenEmisionActiva(resumenPorCompania)
            setUrlExcel(null)
            setSeleccionados({})
            buscar()
            iniciarPollingEstadoEmision(dataEmisionPagos.emisionRealizada.idEmision)
        }
    }, [dataEmisionPagos.emisionRealizada])

    useEffect(() => {
        if (dataEmisionPagos.estadoEmision && dataEmisionPagos.estadoEmision.listo) {
            setUrlExcel(dataEmisionPagos.estadoEmision.url)
            detenerPolling()
        }
    }, [dataEmisionPagos.estadoEmision])

    const consultarEstadoEmision = (idEmision) => {
        let query = Buffer.from(JSON.stringify({ accion: 'estadoEmision', idEmision })).toString('base64')
        return dispatch(obtenerEstadoEmisionAction(query))
    }

    const iniciarPollingEstadoEmision = (idEmision) => {
        detenerPolling()
        pollRef.current.intentos = 0
        pollRef.current.timer = setInterval(() => {
            pollRef.current.intentos += 1
            consultarEstadoEmision(idEmision)
            if (pollRef.current.intentos >= POLL_MAX_INTENTOS) {
                detenerPolling()
            }
        }, POLL_INTERVAL_MS)
    }

    const handleRefrescarEstado = () => {
        if (!resultadoEmision) return
        setRefrescandoExcel(true)
        consultarEstadoEmision(resultadoEmision.idEmision)
            .then(() => setRefrescandoExcel(false))
            .catch(() => setRefrescandoExcel(false))
    }

    const handleDescargarExcel = () => {
        if (urlExcel) {
            let link = document.createElement('a')
            link.href = urlExcel
            link.click()
        }
    }

    return (
        <Container>
            <style>{`
                .grilla-emision-pagos .selection-cell-header { background-color: #728bb0; color: white; }
                .modal-reversa-header .btn-close { filter: invert(1) grayscale(1); }
            `}</style>
            <Card className='mt-2'>
                <Card.Header as='h6' style={{ backgroundColor: '#4F517A', color: 'white', textAlign: 'center' }}>
                    Emisión de Pagos Aprobados
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md='3'>
                            <Form.Label style={{ fontWeight: 'bold', fontSize: '14px' }}>Aprobado Desde</Form.Label>
                            <Form.Control type='date' value={filtros.fechaDesde} max={filtros.fechaHasta || undefined} isInvalid={!!errorFechas}
                                onChange={(e) => { setFiltros({ ...filtros, fechaDesde: e.target.value }); setErrorFechas('') }} />
                        </Col>
                        <Col md='3'>
                            <Form.Label style={{ fontWeight: 'bold', fontSize: '14px' }}>Aprobado Hasta</Form.Label>
                            <Form.Control type='date' value={filtros.fechaHasta} min={filtros.fechaDesde || undefined} isInvalid={!!errorFechas}
                                onChange={(e) => { setFiltros({ ...filtros, fechaHasta: e.target.value }); setErrorFechas('') }} />
                            <Form.Control.Feedback type='invalid'>{errorFechas}</Form.Control.Feedback>
                        </Col>
                        <Col md='6'>
                            <Form.Label style={{ fontWeight: 'bold', fontSize: '14px' }}>Compañía Tercero</Form.Label>
                            <Form.Select value={filtros.companiaId}
                                onChange={(e) => setFiltros({ ...filtros, companiaId: e.target.value })}>
                                <option value=''>Seleccione...</option>
                                {listaCompanias.map((c) => (
                                    <option key={c.id} value={c.id}>{c.nombre}</option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>
                    <Row className='mt-3'>
                        <Col className='d-flex justify-content-end'>
                            <Button variant='outline-warning' className='me-2' disabled={cargando} onClick={handleLimpiar}>Limpiar</Button>
                            <Button variant='success' disabled={cargando} onClick={buscar}>
                                {cargando && <Spinner as='span' animation='border' size='sm' className='me-2' />}
                                Buscar
                            </Button>
                        </Col>
                    </Row>

                    <Container className='mt-3 p-0'>
                        <div style={{ backgroundColor: '#4F517A', color: 'white' }} className='px-2 py-1 fw-bold'>
                            Crear Emisión de Pagos
                        </div>
                        <Row className='mt-2'>
                            <Col md='6'>
                                <Row>
                                    <Col md='4'>
                                        <Form.Label style={{ fontWeight: 'bold', fontSize: '14px' }}>Id Emisión</Form.Label>
                                        <Form.Control disabled value={resultadoEmision?.idEmision || ''} />
                                    </Col>
                                    <Col md='4'>
                                        <Form.Label style={{ fontWeight: 'bold', fontSize: '14px' }}>Monto a Emitir</Form.Label>
                                        <Form.Control disabled value={montoMostrado.toLocaleString('es-CL')} />
                                    </Col>
                                    <Col md='4'>
                                        <Form.Label style={{ fontWeight: 'bold', fontSize: '14px' }}>Cantidad Pagos</Form.Label>
                                        <Form.Control disabled value={cantidadMostrada} />
                                    </Col>
                                </Row>
                                <Row className='mt-3'>
                                    <Col className='d-flex align-items-center'>
                                        <OverlayTrigger overlay={<Tooltip>{urlExcel ? 'Descargar excel' : (resultadoEmision ? 'Generando informe…' : 'Disponible luego de emitir')}</Tooltip>}>
                                            <span>
                                                <Button variant='outline-success' className='me-2' disabled={!urlExcel} onClick={handleDescargarExcel}>
                                                    <SiMicrosoftexcel />
                                                </Button>
                                            </span>
                                        </OverlayTrigger>
                                        {resultadoEmision && !urlExcel && (
                                            <Button variant='outline-info' size='sm' disabled={refrescandoExcel} onClick={handleRefrescarEstado}>
                                                Refrescar estado
                                            </Button>
                                        )}
                                    </Col>
                                </Row>
                            </Col>
                            <Col md='6'>
                                {resumenMostrado.length > 0 && (
                                    <Table size='sm' bordered>
                                        <thead>
                                            <tr>
                                                <th style={headerStyleGrilla}>Compañía</th>
                                                <th style={headerStyleGrilla}>Monto Pago</th>
                                                <th style={headerStyleGrilla}>Cnt.Pago</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {resumenMostrado.map((r) => (
                                                <tr key={r.compania}>
                                                    <td>{r.compania}</td>
                                                    <td>{r.monto.toLocaleString('es-CL')}</td>
                                                    <td>{r.cantidad}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Col>
                        </Row>

                        <hr className='mt-3' />
                        <Row className='mt-2'>
                            <Col className='d-flex justify-content-end'>
                                <Button variant='danger' disabled={!(montoEmitir > 0) || emitiendo} onClick={handleEmitirPagosClick}>
                                    {emitiendo && <Spinner as='span' animation='border' size='sm' className='me-2' />}
                                    Emitir Pagos
                                </Button>
                            </Col>
                        </Row>
                    </Container>

                    <Container className='mt-3 p-0'>
                        <div className='d-flex align-items-center px-2 py-1 fw-bold' style={{ backgroundColor: '#4F517A', color: 'white' }}>
                            <span className='flex-grow-1'>Resultado Búsqueda ({filas.length})</span>
                            <OverlayTrigger overlay={<Tooltip>Descargar Excel</Tooltip>}>
                                <span>
                                    <Button variant='outline-light' size='sm' disabled={filas.length === 0} onClick={handleDescargarExcelBusqueda}>
                                        <SiMicrosoftexcel />
                                    </Button>
                                </span>
                            </OverlayTrigger>
                        </div>
                        {cargando ? (
                            <div className='text-center text-muted py-5'>
                                <Spinner animation='border' variant='primary' />
                            </div>
                        ) : (
                            <ToolkitProvider keyField='__key' data={filas.map((row) => ({ ...row, __key: rowKey(row) }))} columns={columns}>
                                {(props) => (
                                    <div className='grilla-emision-pagos'>
                                        <BootstrapTable
                                            {...props.baseProps}
                                            bordered={false}
                                            pagination={paginationFactory({ sizePerPage: 25, sizePerPageList: [{ text: '25', value: 25 }, { text: '50', value: 50 }, { text: '100', value: 100 }] })}
                                            selectRow={{
                                                mode: 'checkbox',
                                                clickToSelect: !resultadoEmision,
                                                nonSelectable: resultadoEmision
                                                    ? filas.map(rowKey)
                                                    : filas.filter(r => !r.indEmitepago).map(rowKey),
                                                nonSelectableStyle: () => ({ backgroundColor: '#e8eaf6', color: '#999' }),
                                                selected: Object.keys(seleccionados).filter((k) => seleccionados[k]),
                                                onSelect: handleSelectRow,
                                                onSelectAll: handleSelectAllRows
                                            }}
                                            noDataIndication={<div className='text-center text-muted fst-italic py-2'>No hay pagos aprobados pendientes de emitir.</div>}
                                        />
                                    </div>
                                )}
                            </ToolkitProvider>
                        )}
                    </Container>
                </Card.Body>
            </Card>

            <Modal show={showConfirm} onHide={() => setShowConfirm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Emisión</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Confirmar emisión de pagos seleccionados (Monto: {montoEmitir.toLocaleString('es-CL')}, Cantidad: {cantidadPagos})..
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={() => setShowConfirm(false)}>Cancelar</Button>
                    <Button variant='primary' onClick={handleConfirmarEmision}>Confirmar</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showReversa} onHide={() => !reversando && setShowReversa(false)} size='lg' backdrop='static'>
                <Modal.Header closeButton className='modal-reversa-header' style={{ backgroundColor: '#4F517A', color: 'white', padding: '10px 16px' }}>
                    <Modal.Title style={{ fontSize: '15px', fontWeight: 'bold' }}>
                        Actualización Respuesta CIA Tercero &nbsp; {filaReversa?.siniestroCIA}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className='px-4 pt-3 pb-2'>
                    <Row className='mb-2'>
                        <Col md='4'>
                            <Form.Label className='fw-bold' style={{ fontSize: '13px' }}>Siniestro</Form.Label>
                            <Form.Control size='sm' disabled value={filaReversa?.siniestroCIA || ''} />
                        </Col>
                        <Col md='5'>
                            <Form.Label className='fw-bold' style={{ fontSize: '13px' }}>CIA Tercero</Form.Label>
                            <Form.Control size='sm' disabled value={filaReversa?.companiaTercero || ''} />
                        </Col>
                        <Col md='3'>
                            <Form.Label className='fw-bold' style={{ fontSize: '13px' }}>Monto Pago</Form.Label>
                            <Form.Control size='sm' disabled value={filaReversa ? Number(filaReversa.montoPago || 0).toLocaleString('es-CL') : ''} />
                        </Col>
                    </Row>
                    <Row className='mb-1'>
                        <Col md='4'>
                            <Form.Label className='fw-bold' style={{ fontSize: '13px' }}>Fecha Respuesta</Form.Label>
                            <Form.Control size='sm' disabled value={filaReversa ? formatearFecha(filaReversa.fecAprobado) : ''} />
                        </Col>
                        <Col md='3'>
                            <Form.Label className='fw-bold' style={{ fontSize: '13px' }}>Días</Form.Label>
                            <Form.Control size='sm' disabled value={filaReversa ? calcularDias(filaReversa.fecAprobado) : ''} />
                        </Col>
                    </Row>

                    <hr style={{ borderColor: '#4F517A', marginTop: '14px', marginBottom: '14px' }} />

                    <Row className='mb-2'>
                        <Col md='7'>
                            <Form.Label className='fw-bold' style={{ fontSize: '13px' }}>Motivo</Form.Label>
                            <Form.Select size='sm'
                                value={opcionRevSeleccionada ? opcionRevSeleccionada.motivoDestino : ''}
                                onChange={(e) => {
                                    const opcion = opcionesReversa.find(o => String(o.motivoDestino) === e.target.value)
                                    setOpcionRevSeleccionada(opcion || null)
                                }}>
                                <option value=''>Seleccione..</option>
                                {opcionesReversa.map((op, i) => (
                                    <option key={i} value={op.motivoDestino}>{op.glosaMotivoDestino || op.motivoDestino}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md='5'>
                            <Form.Label className='fw-bold' style={{ fontSize: '13px' }}>Clasificación</Form.Label>
                            <Form.Control size='sm' disabled
                                value={opcionRevSeleccionada
                                    ? (opcionRevSeleccionada.glosaClasDestino || opcionRevSeleccionada.clasDestino || '')
                                    : ''} />
                        </Col>
                    </Row>
                    <Row className='mb-1'>
                        <Col md='12'>
                            <Form.Label className='fw-bold' style={{ fontSize: '13px' }}>Observaciones</Form.Label>
                            <Form.Control as='textarea' rows={3} maxLength={233}
                                value={obsReversa}
                                onChange={(e) => setObsReversa(e.target.value)} />
                            <Form.Text className='text-muted' style={{ fontSize: '12px' }}>
                                {233 - obsReversa.length} caracteres restantes
                            </Form.Text>
                        </Col>
                    </Row>

                    <hr style={{ borderColor: '#4F517A', marginTop: '14px', marginBottom: '10px' }} />

                    <Row>
                        <Col className='d-flex justify-content-end gap-2'>
                            <Button variant='outline-secondary' disabled={reversando} onClick={() => setShowReversa(false)}>
                                Volver
                            </Button>
                            <Button variant='danger' disabled={!opcionRevSeleccionada || reversando} onClick={handleConfirmarReversa}>
                                {reversando && <Spinner as='span' animation='border' size='sm' className='me-2' />}
                                Actualizar
                            </Button>
                        </Col>
                    </Row>
                </Modal.Body>
            </Modal>
        </Container>
    )
}

export default EmisionPagosCias
